/**
 * PasswordPolicyChecklist
 *
 * Live checklist of password-policy rules that updates as the user types. Shared by the public
 * set-password page and the authenticated change-password dialog. Only rules with a minimum > 0
 * are shown; `passwordSatisfiesPolicy` is the matching gate for enabling submit buttons.
 */

import React from "react";
import { Check, X } from "lucide-react";

import { cn } from "../lib/utils";
import type { PasswordPolicy } from "../lib/api-central-mu";

type PolicyRule = {
  key: keyof PasswordPolicy;
  label: (n: number) => string;
  count: (password: string) => number;
};

const RULES: PolicyRule[] = [
  {
    key: "minimumPasswordLength",
    label: (n) => `At least ${n} character${n === 1 ? "" : "s"}`,
    count: (pw) => pw.length,
  },
  {
    key: "minimumUppercaseCharacters",
    label: (n) => `At least ${n} uppercase letter${n === 1 ? "" : "s"}`,
    count: (pw) => (pw.match(/[A-Z]/g) ?? []).length,
  },
  {
    key: "minimumLowercaseCharacters",
    label: (n) => `At least ${n} lowercase letter${n === 1 ? "" : "s"}`,
    count: (pw) => (pw.match(/[a-z]/g) ?? []).length,
  },
  {
    key: "minimumNumericCharacters",
    label: (n) => `At least ${n} number${n === 1 ? "" : "s"}`,
    count: (pw) => (pw.match(/[0-9]/g) ?? []).length,
  },
  {
    key: "minimumSpecialCharacters",
    label: (n) => `At least ${n} special character${n === 1 ? "" : "s"}`,
    count: (pw) => (pw.match(/[^A-Za-z0-9]/g) ?? []).length,
  },
];

/** Rules that apply for this policy (minimum > 0). */
const activeRules = (policy: PasswordPolicy) =>
  RULES.filter((rule) => (policy[rule.key] ?? 0) > 0);

/** True when the password meets every active rule of the policy. */
export function passwordSatisfiesPolicy(policy: PasswordPolicy | undefined, password: string): boolean {
  if (!policy) return false;
  return activeRules(policy).every((rule) => rule.count(password) >= policy[rule.key]);
}

export function PasswordPolicyChecklist({
  policy,
  password,
  className,
}: {
  policy: PasswordPolicy;
  password: string;
  className?: string;
}) {
  const rules = activeRules(policy);
  if (rules.length === 0) return null;

  return (
    <ul className={cn("space-y-1", className)}>
      {rules.map((rule) => {
        const required = policy[rule.key];
        const met = rule.count(password) >= required;
        return (
          <li
            key={rule.key}
            className={cn(
              "flex items-center gap-2 text-xs transition-colors",
              met ? "text-emerald-600" : "text-muted-foreground"
            )}
          >
            {met ? <Check className="size-3.5 shrink-0" /> : <X className="size-3.5 shrink-0" />}
            {rule.label(required)}
          </li>
        );
      })}
    </ul>
  );
}
